import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewsApi } from '../../../api/reviews';
import { syncRelatedAlbumsCache } from './useRelatedAlbums';
import { useUserStore } from '../../../stores/userStore';

export const useAlbumReviews = (albumSource, sourceId) =>
  useQuery({
    queryKey: ['albumReviews', albumSource, sourceId],
    queryFn: () =>
      albumSource === 'local'
        ? reviewsApi.getAlbumReviews(sourceId)
        : reviewsApi.getAlbumReviewsBySource(sourceId, albumSource),
    select: (res) => res.data,
    enabled: !!sourceId,
  });

export const useReviewById = (reviewId, enabled = true) =>
  useQuery({
    queryKey: ['review', reviewId],
    queryFn: () => reviewsApi.getReviewById(reviewId),
    select: (res) => res.data,
    enabled: !!reviewId && enabled,
  });

export const useCreateReview = (albumSource, sourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ albumId, content, rating }) => {
      if (albumSource === 'apple_music') {
        const otherVersionsData = queryClient.getQueryData([
          'otherVersions',
          sourceId,
          albumSource.toUpperCase(),
        ]);
        const otherVersionSourceIds = otherVersionsData?.data?.map((v) => v.sourceId) || [];
        return reviewsApi.postAppleMusicReview(sourceId, content, rating, otherVersionSourceIds);
      }
      return reviewsApi.postReview(albumId, content, rating);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['albumReviews'] });
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
      queryClient
        .invalidateQueries({ queryKey: ['albumDetail'] })
        .then(() => syncRelatedAlbumsCache(queryClient, albumSource, sourceId));
      void useUserStore.getState().fetchUser(); // 리뷰 개수 충족으로 자동등업 된 경우 등급 정보 최신화를 위해
    },
  });
};

export const useUpdateReview = (albumSource, sourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ albumId, reviewId, content, rating }) => {
      return reviewsApi.updateReview(albumId, reviewId, content, rating);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['albumReviews'] });
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
      queryClient.invalidateQueries({ queryKey: ['review', variables.reviewId] });
      queryClient
        .invalidateQueries({ queryKey: ['albumDetail'] })
        .then(() => syncRelatedAlbumsCache(queryClient, albumSource, sourceId));
    },
  });
};

export const useDeleteReview = (albumSource, sourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ albumId, reviewId }) => {
      return reviewsApi.deleteReview(albumId, reviewId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['album-review-history'] });
      queryClient.invalidateQueries({ queryKey: ['albumReviews'] });
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
      queryClient
        .invalidateQueries({ queryKey: ['albumDetail'] })
        .then(() => syncRelatedAlbumsCache(queryClient, albumSource, sourceId));
    },
  });
};

export const useToggleReviewLike = (albumSource, sourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reviewId, isLiked }) => {
      if (isLiked) {
        await reviewsApi.deleteLike(reviewId);
      } else {
        await reviewsApi.addLike(reviewId);
      }
      return { reviewId, isLiked };
    },
    onMutate: async ({ reviewId, isLiked }) => {
      await queryClient.cancelQueries({ queryKey: ['albumReviews', albumSource, sourceId] });
      await queryClient.cancelQueries({ queryKey: ['review', reviewId] });

      const previousAlbumReviews = queryClient.getQueryData([
        'albumReviews',
        albumSource,
        sourceId,
      ]);
      const previousReview = queryClient.getQueryData(['review', reviewId]);

      const likeUpdate = (data) => ({
        ...data,
        isLiked: !isLiked,
        likeCount: isLiked ? data.likeCount - 1 : data.likeCount + 1,
      });

      queryClient.setQueryData(['albumReviews', albumSource, sourceId], (old) => {
        if (!old) return old;
        return { ...old, data: old.data.map((r) => (r.id === reviewId ? likeUpdate(r) : r)) };
      });

      queryClient.setQueryData(['review', reviewId], (old) => {
        if (!old) return old;
        return { ...old, data: likeUpdate(old.data) };
      });

      return { previousAlbumReviews, previousReview, reviewId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAlbumReviews) {
        queryClient.setQueryData(
          ['albumReviews', albumSource, sourceId],
          context.previousAlbumReviews,
        );
      }
      if (context?.previousReview) {
        queryClient.setQueryData(['review', context.reviewId], context.previousReview);
      }
    },
  });
};

export const useToggleReviewBookmark = (albumSource, sourceId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reviewId, isBookmarked }) => {
      if (isBookmarked) {
        await reviewsApi.deleteReviewBookmark(reviewId);
      } else {
        await reviewsApi.addReviewBookmark(reviewId);
      }
      return { reviewId, isBookmarked };
    },
    onMutate: async ({ reviewId, isBookmarked }) => {
      await queryClient.cancelQueries({ queryKey: ['albumReviews', albumSource, sourceId] });
      await queryClient.cancelQueries({ queryKey: ['review', reviewId] });

      const previousAlbumReviews = queryClient.getQueryData([
        'albumReviews',
        albumSource,
        sourceId,
      ]);
      const previousReview = queryClient.getQueryData(['review', reviewId]);

      const bookmarkUpdate = (data) => ({
        ...data,
        isBookmarked: !isBookmarked,
        bookmarkCount: isBookmarked
          ? Math.max((data.bookmarkCount || 0) - 1, 0)
          : (data.bookmarkCount || 0) + 1,
      });

      queryClient.setQueryData(['albumReviews', albumSource, sourceId], (old) => {
        if (!old) return old;
        return { ...old, data: old.data.map((r) => (r.id === reviewId ? bookmarkUpdate(r) : r)) };
      });

      queryClient.setQueryData(['review', reviewId], (old) => {
        if (!old) return old;
        return { ...old, data: bookmarkUpdate(old.data) };
      });

      return { previousAlbumReviews, previousReview, reviewId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAlbumReviews) {
        queryClient.setQueryData(
          ['albumReviews', albumSource, sourceId],
          context.previousAlbumReviews,
        );
      }
      if (context?.previousReview) {
        queryClient.setQueryData(['review', context.reviewId], context.previousReview);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['albumReviews', albumSource, sourceId] });
      queryClient.invalidateQueries({ queryKey: ['review', variables.reviewId] });
      queryClient.invalidateQueries({ queryKey: ['myBookmarks'] });
    },
  });
};
